import type {
  ConvertExitCurrenciesResponse,
  ExitsInvestmentsToCompleteResponse,
  IssueCountResponse,
  LastCleanResponse,
  MixedCurrencyExitProfilesResponse,
} from '@shared/schemas';

import api from '@/shared/api/api-client';

import type {
  BrokenUrlProfile,
  DeleteBrokenUrlsResult,
  DuplicateGroup,
  MergeGroupResult,
} from '../domain/profile-cleaning.model';
import type { ProfileCleaningGateway } from './profile-cleaning.gateway';

interface ListEnvelope<T> {
  data: T[];
  total: number;
}

/**
 * Real implementation over the shared api client: the single place that knows the
 * admin Profile Cleaning endpoints and request shapes (kept 1:1 with the original
 * component). The list endpoints wrap their rows in a `{ data, total }` payload.
 */
export const httpProfileCleaningGateway: ProfileCleaningGateway = {
  loadBrokenUrlProfiles: async () => {
    const res = await api.get<ListEnvelope<BrokenUrlProfile>>('/dead-profiles');
    return res.data;
  },

  loadDuplicateGroups: async () => {
    const res = await api.get<ListEnvelope<DuplicateGroup>>('/duplicate-profiles');
    return res.data;
  },

  loadMixedCurrencyExitProfiles: async () => {
    const res = await api.get<MixedCurrencyExitProfilesResponse>('/mixed-currency-exit-profiles');
    return res.data;
  },

  loadExitsInvestmentsToCompleteProfiles: async () => {
    const res = await api.get<ExitsInvestmentsToCompleteResponse>(
      '/exits-investments-to-complete-profiles',
    );
    return res.data;
  },

  getIssueCount: async () =>
    (await api.get<IssueCountResponse['data']>('/admin/profile-cleaning/issue-count')).count,
  loadLastClean: async () => {
    const res = await api.get<LastCleanResponse>('/profile-cleaning/last-clean');
    return res.last_clean;
  },

  deleteBrokenUrlProfilesByIds: (profileIds) =>
    api.post<DeleteBrokenUrlsResult>('/dead-profiles/delete', { profile_ids: profileIds }),

  mergeGroup: (canonicalId, duplicateIds) =>
    api.post<MergeGroupResult>('/duplicate-profiles/merge', {
      canonical_id: canonicalId,
      duplicate_ids: duplicateIds,
    }),

  convertExitCurrencies: (profileId, request) =>
    api.post<ConvertExitCurrenciesResponse>(
      `/mixed-currency-exit-profiles/${profileId}/convert`,
      request,
    ),
};
